import {
  Component,
  ElementRef,
  EventEmitter,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import {
  DashboardSearchModel,
  DashboardSearchResponse,
} from '@payments/components/dashboard-search/dashboard-search.component';
import { DashboardSearchTouchComponent } from '@payments/components/dashboard-search/touch/dashboard-search-touch.component';
import { HelpModalComponent } from '@payments/components/help-modal/help-modal.component';
import { TerritoryTile } from '@payments/components/territory-tile/model/territory-tile.model';
import { HelpTerms } from '@payments/constants/help-for-seller-dashboard';
import {
  CustomerSellerMetrics,
  PaymentDashboardPageViewModel,
} from '@payments/models/payment-dashboard-page-view.model';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import {
  AllFilterIds,
  ModalType,
  PaymentsModalService,
} from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { ModalService } from '@shared/services/modal/modal.service';
import { SortDirection, SortFilter } from '@usf/ngrx-payment';
import { CustomerMetrics } from '@usf/payment-types';
import { BehaviorSubject, filter, Observable, Subscription, take } from 'rxjs';
import {
  DashboardFilter,
  DashboardFilterIds,
  DashboardSection,
  PaymentDashboardService,
} from './services/payment-dashboard.service';

import { Customer } from '@usf/customer-types/customer';
import { DsmInfo } from '@usf/ngrx-order/lib/models/client/dsm-info';
import {
  TerritorySelectionComponent,
  TerritorySelectionResult,
} from './territory-selection/territory-selection.component';
import { PaymentDashboardAnalyticsService } from './tracking/analytics/payment-dashboard-analytics.service';

@Component({
  selector: 'app-payment-dashboard',
  templateUrl: './payment-dashboard.page.html',
  styleUrls: ['./payment-dashboard.page.scss'],
})
export class PaymentDashboardPage extends PaymentsBasePage {
  @ViewChild('territoryTiles') territoryTiles: ElementRef;
  private caretDropdown: HTMLElement;

  readonly backgroundImageStyle = "url('/assets/images/payments-hero.webp')";
  readonly DashboardSection = DashboardSection;
  readonly overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.8) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0.5) 40%, rgba(47,47,47,0.3) 53%, rgba(47,47,47,0) 77%, rgba(47,47,47,0) 89%, rgba(47,47,47,0) 100%)';

  allCustomersMetrics: CustomerSellerMetrics[] = [];
  currentLoadingIndex = 0;
  currentSection = DashboardSection.accountActivity;
  dashboardSearchRootPage = DashboardSearchTouchComponent;
  dashboardSearchParams: DashboardSearchModel;
  vmSubscription$: Subscription;
  isDesktop: boolean;
  isSearchModalOpen$ = new BehaviorSubject<boolean>(false);
  isTerritoryTilesExpanded = true;
  loadingKeys = ['gatheringData', 'analyzingTrends', 'getReady'];
  loadingTexts: {};
  loadingTimeout: ReturnType<typeof setTimeout>;
  loadingTimings = [5, 10, 10];
  paymentDashboardVM$: Observable<PaymentDashboardPageViewModel>;
  searchText = '';
  selectedTerritoryTile: TerritoryTile;
  templateSetFiltersEmitter = new EventEmitter<DashboardSearchResponse>();
  customerMetrics: CustomerMetrics[];
  isVirtualScrollReloading = false;
  territorySelectionResult: TerritorySelectionResult = {
    wasCancelled: false,
    selectedTerritories: [],
  };
  isLoading = true;
  vsRowBuffer = 10;

  tmSelectionText: string;
  tmSelectionCount: number;

  private getTmSelectionCount(): number {
    const selectedTerritories =
      this.territorySelectionResult.selectedTerritories.length;
    if (!selectedTerritories) {
      return this.allCustomersMetrics.length;
    } else {
      return selectedTerritories;
    }
  }

  private getTmSelectionText(): string {
    const selectedTerritories =
      this.territorySelectionResult.selectedTerritories;
    if (!selectedTerritories.length) {
      return 'i18n.payments.paymentDashboard.myCustomers';
    } else if (selectedTerritories.length == 1) {
      const seller = selectedTerritories[0];
      return [seller.firstName, seller.lastName]
        .filter(name => name.length)
        .map(name => name.charAt(0).toUpperCase())
        .join('');
    } else {
      return 'i18n.payments.paymentDashboard.mySellers';
    }
  }

  constructor(
    public router: Router,
    private renderer: Renderer2,
    readonly panAppState: PanAppState,
    readonly paymentDashboardService: PaymentDashboardService,
    readonly paymentsService: PaymentsService,
    readonly platformService: PlatformService,
    readonly route: ActivatedRoute,
    readonly translateService: TranslateService,
    readonly modalService: ModalService,
    readonly modalController: ModalController,
    readonly paymentsModalService: PaymentsModalService,
    readonly paymentDashboardAnalytics: PaymentDashboardAnalyticsService,
  ) {
    super(router, route, paymentsService);
  }

  ionViewWillEnter(): void {
    super.ionViewWillEnter();
    this.paymentDashboardService.getInitialData();
    this.paymentDashboardVM$ =
      this.paymentDashboardService.getPaymentDashboardViewModel$();
    this.isDesktop =
      this.platformService.getPlatformType() ===
      this.platformService.platformEnum.desktop;
    const pageTitle =
      this.platformService.platformType === PlatformEnum.desktop
        ? PageTitle.paymentDashboard
        : PageTitle.invoicesAndPayments;
    this.panAppState.setPageTitle(this.translateService.instant(pageTitle));

    this.vmSubscription$ = this.paymentDashboardVM$.subscribe(vm => {
      this.customerMetrics = vm.customerMetrics;
      if (vm.customerMetrics?.length === this.allCustomersMetrics.length) {
        this.allCustomersMetrics = vm.customerMetrics;
      }

      this.tmSelectionText = this.getTmSelectionText();
      this.tmSelectionCount = this.getTmSelectionCount();
      this.refreshVirtualScroll();
    });

    this.paymentDashboardVM$
      .pipe(
        filter(vm => !vm.isLoading),
        take(1),
      )
      .subscribe(vm => {
        this.allCustomersMetrics = vm.customerMetrics;
        this.tmSelectionCount = this.getTmSelectionCount();
        this.dashboardSearchParams = {
          customerMetrics: this.allCustomersMetrics,
          setFilters: (response: DashboardSearchResponse) => {
            this.setFilters(response);
            this.openSearchModal(false);
          },
        };
        clearTimeout(this.loadingTimeout);
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      });

    this.setLoadingText();
    this.isLoading = true;
  }

  ionViewDidEnter() {
    this.paymentDashboardAnalytics.trackPageLoad();
  }

  ionViewDidLeave() {
    clearTimeout(this.loadingTimeout);
    this.vmSubscription$?.unsubscribe();
  }

  triggerTileAction(tile: TerritoryTile) {
    if (tile.action?.page) {
      this.paymentDashboardAnalytics.trackOutboundLink(
        tile.action.page +
          Object.entries(tile.action.query).reduce(
            (prev, [key, value], index, array) => {
              return (
                `${prev}${key}=${value}` +
                (array.length - 1 != index ? '&' : '')
              );
            },
            '?',
          ),
      );

      this.routeTo(tile.action.page, tile.action.query);
    } else {
      this.setTileFilter(tile);
    }
  }

  redirectClick() {
    if (!this.caretDropdown) {
      this.caretDropdown = document.getElementById('dashboard-caret-dropdown');
    }
    this.caretDropdown?.click();
  }

  async presentTerritorySelectionPopover($event: Event, dsmInfo: DsmInfo[]) {
    $event.stopPropagation();
    const myCustomersAction = () => {
      this.territorySelectionResult.selectedTerritories = [];
      this.reloadSelectedCustomerMetrics();
    };
    const mySellersAction = () => this.presentTerritorySelection(dsmInfo);

    this.paymentsModalService.presentTerritoryFilterOptions(
      $event,
      myCustomersAction,
      mySellersAction,
    );
  }

  async presentTerritorySelection(dsmInfo: DsmInfo[]) {
    let customerSelectionElement: HTMLIonModalElement | HTMLIonPopoverElement;
    const props = {
      dsmInfo,
      preSelectedTerritories:
        this.territorySelectionResult?.selectedTerritories ?? [],
    };

    if (this.isDesktop) {
      //Desktop Customer Selector
      this.modalService.setModalOptions(
        false,
        TerritorySelectionComponent,
        props,
        'select-customers-desktop-modal',
      );
      customerSelectionElement = await this.modalController.create(
        this.modalService.modalOptions,
      );
    } else {
      // Mobile Customer Selector
      this.modalService.setModalOptions(
        true,
        TerritorySelectionComponent,
        props,
      );
      customerSelectionElement = await this.modalController.create(
        this.modalService.modalOptions,
      );
    }

    await customerSelectionElement.present();

    await customerSelectionElement.onDidDismiss().then((resp: any) => {
      if (!resp.data.wasCancelled) {
        this.territorySelectionResult = resp.data;
        this.reloadSelectedCustomerMetrics();
      }
    });
  }

  private reloadSelectedCustomerMetrics() {
    let customersToLoad: number[];

    if (this.hasSellersSelected) {
      customersToLoad = this.getCustomersForTMSelection().map(
        customer => customer.customerNumber,
      );
    }

    this.paymentDashboardService.reloadCustomerMetricsForCustomers(
      customersToLoad,
    );
  }

  handleSearchClick(event: any) {
    event.currentTarget?.blur();
    this.openSearchModal();
  }

  handleSearchBlur(_event: any) {
    this.paymentDashboardAnalytics.trackSearch(this.searchText);
  }

  openSearchModal(isOpen = true) {
    this.isSearchModalOpen$.next(isOpen);
    if (isOpen) {
      this.searchText = '';
    }
  }

  searchModalClosed(_event: any) {
    this.isSearchModalOpen$.next(false);
    this.paymentDashboardAnalytics.trackSearch(this.searchText);
  }

  openModal(selectedFilter?: AllFilterIds, isDsmDashboardOn?: boolean) {
    this.paymentsModalService.openModal(
      this.paymentDashboardService,
      selectedFilter,
      false,
      ModalType.sellerDashboard,
      false,
      this.hasSellersSelected,
      this.getCustomersForTMSelection(),
      isDsmDashboardOn,
    );
    this.searchText = '';
    this.paymentDashboardAnalytics.trackModalOpen();
  }

  get hasSellersSelected(): boolean {
    return !!this.territorySelectionResult?.selectedTerritories?.length;
  }

  private getCustomersForTMSelection(): Customer[] {
    let customers: Customer[] = [];
    this.territorySelectionResult?.selectedTerritories.forEach(territory => {
      let initials =
        territory.firstName?.charAt(0)?.toUpperCase() +
        territory.lastName?.charAt(0)?.toUpperCase();
      this.allCustomersMetrics.forEach(customer => {
        if (customer.seller === initials) {
          customers.push(customer as unknown as Customer);
        }
      });
    });
    return customers;
  }

  resetFilters() {
    this.searchText = '';
    if (this.selectedTerritoryTile) {
      this.selectedTerritoryTile.isActive = false;
      this.selectedTerritoryTile = undefined;
    }
    this.paymentDashboardService.clearFiltersAndResetSort();
  }

  listToggleChange() {
    this.refreshVirtualScroll();
  }

  private refreshVirtualScroll() {
    this.isVirtualScrollReloading = true;
    // By changing this value we can force the reload of the virtual scroll
    this.vsRowBuffer = 0;
    setTimeout(() => {
      this.isVirtualScrollReloading = false;
      this.vsRowBuffer =
        this.customerMetrics.length > 10 ? 10 : this.customerMetrics.length;
    }, 0);
  }

  setFilters(response: DashboardSearchResponse) {
    this.paymentDashboardVM$.pipe(take(1)).subscribe(_ => {
      let newFilters = response.items.map(item => {
        return {
          filterName: item.filter,
          value: item.value,
        } as DashboardFilter;
      });
      if (response.items.length === 0) {
        newFilters = [
          {
            filterName: DashboardFilterIds.customer,
            value: [],
          },
        ];
      }
      this.paymentDashboardService.setFilter(newFilters);
    });

    this.searchText = response.searchText || '';
  }

  toggleTerritoryExpansion() {
    this.isTerritoryTilesExpanded = !this.isTerritoryTilesExpanded;
    if (this.isTerritoryTilesExpanded) {
      this.renderer?.setStyle(
        this.territoryTiles?.nativeElement,
        'height',
        'auto',
      );
    } else {
      const height = this.territoryTiles?.nativeElement.clientHeight;
      this.renderer?.setStyle(
        this.territoryTiles?.nativeElement,
        'height',
        height / 2 - 4 + 'px',
      );
    }
  }

  private setTileFilter(tile: TerritoryTile) {
    if (tile === this.selectedTerritoryTile) {
      this.selectedTerritoryTile = undefined;
      tile.isActive = false;
      this.paymentDashboardService.setFilter([]);
    } else {
      if (this.selectedTerritoryTile != undefined)
        this.selectedTerritoryTile.isActive = false;
      this.selectedTerritoryTile = tile;
      tile.isActive = true;
      this.paymentDashboardAnalytics.trackTileClick(
        this.translateService.instant(tile.title),
      );
      this.paymentDashboardService.setFilter([
        tile.action.query as DashboardFilter,
      ]);
    }
  }

  private setLoadingText() {
    this.currentLoadingIndex = 0;

    this.translateService
      .get('i18n.payments.paymentDashboard.title')
      .pipe(take(1))
      ?.subscribe(_ => {
        this.loadingTexts = this.translateService.instant(
          'i18n.payments.paymentDashboard.loadingTexts',
        );
        this.changeLoadingText();
      });
  }

  private changeLoadingText() {
    clearTimeout(this.loadingTimeout);
    this.loadingTimeout = setTimeout(() => {
      if (
        this.currentLoadingIndex <
        Object.keys(this.loadingTexts).length - 1
      ) {
        this.currentLoadingIndex += 1;
        this.changeLoadingText();
      } else {
        clearTimeout(this.loadingTimeout);
      }
    }, this.loadingTimings[this.currentLoadingIndex] * 1000);
  }

  async openHelpModal() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      HelpModalComponent,
      {
        terms: HelpTerms,
        sectionName: 'i18n.payments.paymentDashboard.title',
      },
      'help-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    return await modal.onDidDismiss();
  }

  setSort(event: SortFilter) {
    let sort: SortFilter[] = [
      { fieldName: event.fieldName, sortValue: event.sortValue },
    ];
    if (event.fieldName === DashboardFilterIds.creditStatus) {
      sort.push({
        fieldName: DashboardFilterIds.customer,
        sortValue: SortDirection.ASC,
      });
    }
    this.paymentDashboardService.setSort(sort);
  }

  ionViewWillLeave(): void {
    this.vmSubscription$?.unsubscribe();
  }
}
